import React, { ReactNode } from "react";
import { Command } from "~/components/guides/components";
import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Select,
  useTheme,
} from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { JiraAutoConfigurationSection } from "~/pages/integrations/hosted-integrations/forms/jira/JiraAutoConfigurationSection";
import { AzureDevOpsFormInput } from "~/pages/integrations/hosted-integrations/forms/azure-devops/AzureDevOpsIntegrationForm";
import { motion } from "motion/react";
import { SubField } from "~/components/Form/components";
import { AzureDevOpsFormattedDestination } from "~/pages/integrations/hosted-integrations/forms/azure-devops/components/AzureDevOpsFormattedDestination";
import { AzureDevopsProject, IntegrationType } from "~/operations";

interface ZendeskFormStepTwoProps {
  control: Control<AzureDevOpsFormInput>;
  errors: FieldErrors<AzureDevOpsFormInput>;
  isValid: boolean;
  isUpdateFlow: boolean;
  disabled: boolean;
  isProjectsEmpty: boolean;
  projects: AzureDevopsProject[] | null | undefined;
}

export const AzureDevOpsFormStepTwo = ({
  control,
  isValid,
  isUpdateFlow,
  disabled,
  projects,
  isProjectsEmpty,
}: ZendeskFormStepTwoProps) => {
  const theme = useTheme();

  return (
    <>
      <Box pb={4}>
        <Command
          number={4}
          options={{
            fontSize: { xs: 16 },
            dotColor: theme.palette.background.lightest,
          }}
        >
          Configure preferences
        </Command>
        <Box mt={3} pl={6}>
          <JiraAutoConfigurationSection
            ticketType={IntegrationType.TicketSystemAzureDevops}
            control={control}
            showAutoCreateSection={true}
            autoCreateSubField={
              <Box
                component={motion.div}
                initial={false}
                transition={{
                  duration: 0.35,
                  staggerChildren: 0.25,
                }}
                sx={{ overflow: "hidden" }}
              >
                <Controller
                  name="defaultProject"
                  {...{ control }}
                  render={({ field }) => (
                    <SubField
                      configuration="Select a default drift issue destination"
                      caption="Select where to create an issue for each new drift case."
                      disabled={disabled}
                      field={
                        <Grid
                          sx={{ alignSelf: "flex-end" }}
                          size={{
                            xs: 12,
                            md: 6,
                          }}
                        >
                          <Select
                            {...field}
                            fullWidth
                            disabled={disabled}
                            sx={{
                              "&.MuiInputBase-root": {
                                bgcolor: "code.background",
                              },
                            }}
                            displayEmpty
                            renderValue={(selected: ReactNode) => {
                              const selectedProject = projects?.find(
                                (project) => project.name === selected,
                              );

                              const selectedProjectTitle = (
                                <AzureDevOpsFormattedDestination
                                  projectTitle={selectedProject?.name || ""}
                                />
                              );

                              if (isProjectsEmpty) {
                                return (
                                  <>
                                    No projects available – create one in Azure
                                    DevOps first
                                  </>
                                );
                              }

                              if (!selected || !selectedProjectTitle) {
                                return <>Select project</>;
                              }

                              return <>{selectedProjectTitle}</>;
                            }}
                          >
                            {(projects || []).map((project) => (
                              <MenuItem key={project.name} value={project.name}>
                                {project.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                      }
                    />
                  )}
                />
              </Box>
            }
          />
        </Box>
      </Box>
      <Box width={1}>
        <Divider />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <Button
          sx={{ textTransform: "uppercase" }}
          type="submit"
          variant="contained"
          color="primary"
          endIcon={<ChevronRightIcon />}
          disabled={!isValid}
        >
          Finalize {isUpdateFlow ? "Update" : "Setup"}
        </Button>
      </Box>
    </>
  );
};
